import { useEffect, useMemo, useState } from "react";
import { Avatar, Badge, Breadcrumb, Dropdown, Input, Layout, Menu, Tooltip, theme } from "antd";
import {
  AppstoreOutlined,
  AuditOutlined,
  BellOutlined,
  DashboardOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  SafetyCertificateOutlined,
  SearchOutlined,
  SettingOutlined,
  ShopOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { ADMIN_ROLES } from "@glow-price/shared";
import type { MenuProps } from "antd";

import { clearSession, getSessionUser, subscribeAuth } from "../auth/store";
import { useCurrentAdmin } from "../auth/useCurrentAdmin";

const { Header, Sider, Content } = Layout;

const APP_VERSION = "v0.3";

// Phase 6D-2 owns this list. 4 业务分组 + 概览。每组用 SubMenu 包起来。
type MenuItem = NonNullable<MenuProps["items"]>[number];
const baseItems: MenuItem[] = [
  // Phase 7: Dashboard is the default landing page (root `/`). Keep the
  // legacy `/dashboard` route working in the router for bookmarks.
  { key: "/", icon: <DashboardOutlined />, label: <Link to="/">概览</Link> },
  { type: "divider" as const },
  {
    key: "group:content",
    label: "内容管理",
    type: "group",
    children: [
      { key: "/brands", label: <Link to="/brands">品牌</Link> },
      { key: "/categories", label: <Link to="/categories">分类</Link> },
      { key: "/standard-products", label: <Link to="/standard-products">标准商品</Link> },
      { key: "/editorial-tags", label: <Link to="/editorial-tags">编辑精选 / 标签</Link> },
      { key: "/home-editorial", label: <Link to="/home-editorial">首页运营</Link> },
    ],
  },
  { type: "divider" as const },
  {
    key: "group:channels",
    label: "渠道与采集",
    type: "group",
    children: [
      { key: "/channels", label: <Link to="/channels">渠道</Link> },
      { key: "/channel-products", label: <Link to="/channel-products">渠道商品</Link> },
      {
        key: "/channel-products-matrix",
        label: <Link to="/channel-products-matrix">渠道商品矩阵</Link>,
      },
      { key: "/channel-scrapers", label: <Link to="/channel-scrapers">采集管理</Link> },
      { key: "/price-snapshots", label: <Link to="/price-snapshots">价格快照</Link> },
      { key: "/flash-sales", label: <Link to="/flash-sales">限时秒杀</Link> },
      { key: "/affiliate-networks", label: <Link to="/affiliate-networks">联盟平台</Link> },
      {
        key: "/channel-affiliate-configs",
        label: <Link to="/channel-affiliate-configs">渠道联盟配置</Link>,
      },
    ],
  },
  { type: "divider" as const },
  {
    key: "group:ops",
    label: "运营",
    type: "group",
    children: [
      { key: "/outbound-clicks", label: <Link to="/outbound-clicks">导购点击</Link> },
      { key: "/affiliate-orders", label: <Link to="/affiliate-orders">联盟订单</Link> },
      {
        key: "/commission-settlements",
        label: <Link to="/commission-settlements">佣金结算</Link>,
      },
      { key: "/feedback", label: <Link to="/feedback">用户反馈</Link> },
      { key: "/audit-logs", label: <Link to="/audit-logs">审计日志</Link> },
    ],
  },
  { type: "divider" as const },
  {
    key: "group:system",
    label: "系统",
    type: "group",
    children: [
      { key: "/legal-docs", label: <Link to="/legal-docs">法务文档</Link> },
      { key: "/system-settings", label: <Link to="/system-settings">系统设置</Link> },
    ],
  },
];

// ─── breadcrumb metadata ───────────────────────────────────────────────────
// Maps a route path to its 分组 / 页面 label so the header breadcrumb reads
// like 「首页 / 渠道与采集 / 采集管理」. Kept beside the menu so the two stay
// in sync.
const GROUP_LABEL: Record<string, string> = {
  "group:content": "内容管理",
  "group:channels": "渠道与采集",
  "group:ops": "运营",
  "group:system": "系统",
  "group:access": "权限管理",
};

const ROUTE_META: Record<string, { group?: string; label: string }> = {
  "/": { label: "概览" },
  "/dashboard": { label: "概览" },
  "/brands": { group: "group:content", label: "品牌" },
  "/categories": { group: "group:content", label: "分类" },
  "/standard-products": { group: "group:content", label: "标准商品" },
  "/editorial-tags": { group: "group:content", label: "编辑精选 / 标签" },
  "/home-editorial": { group: "group:content", label: "首页运营" },
  "/channels": { group: "group:channels", label: "渠道" },
  "/channel-products": { group: "group:channels", label: "渠道商品" },
  "/channel-products-matrix": { group: "group:channels", label: "渠道商品矩阵" },
  "/channel-scrapers": { group: "group:channels", label: "采集管理" },
  "/price-snapshots": { group: "group:channels", label: "价格快照" },
  "/flash-sales": { group: "group:channels", label: "限时秒杀" },
  "/affiliate-networks": { group: "group:channels", label: "联盟平台" },
  "/channel-affiliate-configs": { group: "group:channels", label: "渠道联盟配置" },
  "/outbound-clicks": { group: "group:ops", label: "导购点击" },
  "/affiliate-orders": { group: "group:ops", label: "联盟订单" },
  "/commission-settlements": { group: "group:ops", label: "佣金结算" },
  "/feedback": { group: "group:ops", label: "用户反馈" },
  "/audit-logs": { group: "group:ops", label: "审计日志" },
  "/legal-docs": { group: "group:system", label: "法务文档" },
  "/system-settings": { group: "group:system", label: "系统设置" },
  "/admin-users": { group: "group:access", label: "账号管理" },
  "/roles": { group: "group:access", label: "角色权限" },
  "/me/change-password": { label: "修改密码" },
};

// Group-level icons for the grouped sider menu.
const GROUP_ICON: Record<string, JSX.Element> = {
  "group:content": <AppstoreOutlined />,
  "group:channels": <ShopOutlined />,
  "group:ops": <AuditOutlined />,
  "group:system": <SettingOutlined />,
  "group:access": <SafetyCertificateOutlined />,
};

export function DashboardLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { token } = theme.useToken();
  const [collapsed, setCollapsed] = useState(false);
  const [user, setUser] = useState(getSessionUser());
  // Phase 6D-1: kick off the /me fetch + cache so RoleGuard / menus can
  // render synchronously from anywhere in the tree.
  const me = useCurrentAdmin();

  useEffect(() => subscribeAuth(() => setUser(getSessionUser())), []);

  // Append the superadmin-only "账号管理" entry at the bottom of the sider
  // without touching the business menu above. Also hide the "用户反馈" entry
  // (superadmin / editor only) from viewers.
  const items = useMemo<MenuProps["items"]>(() => {
    const canSeeFeedback =
      me?.role === ADMIN_ROLES.SUPERADMIN || me?.role === ADMIN_ROLES.EDITOR;
    const filtered = (baseItems as MenuItem[]).map((item) => {
      // Attach a group icon so each business section reads at a glance.
      if (item && "type" in item && item.type === "group" && "key" in item) {
        const icon = GROUP_ICON[String(item.key)];
        const children =
          item.key === "group:ops" && "children" in item
            ? (item.children ?? []).filter(
                (child) =>
                  canSeeFeedback ||
                  !(child && "key" in child && child.key === "/feedback"),
              )
            : (item.children ?? []);
        return {
          ...item,
          label: icon ? (
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              {icon}
              <span>{item.label as React.ReactNode}</span>
            </span>
          ) : (
            item.label
          ),
          children,
        } as MenuItem;
      }
      return item;
    });
    if (me?.role !== ADMIN_ROLES.SUPERADMIN) return filtered;
    return [
      ...filtered,
      { type: "divider" as const },
      {
        key: "group:access",
        type: "group" as const,
        label: (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            {GROUP_ICON["group:access"]}
            <span>权限管理</span>
          </span>
        ),
        children: [
          {
            key: "/admin-users",
            label: <Link to="/admin-users">账号管理</Link>,
          },
          {
            key: "/roles",
            label: <Link to="/roles">角色权限</Link>,
          },
        ],
      },
    ];
  }, [me?.role]);

  // ─── route-driven breadcrumb ─────────────────────────────────────────────
  const breadcrumbItems = useMemo(() => {
    const meta = ROUTE_META[location.pathname];
    const crumbs: { title: React.ReactNode }[] = [
      { title: <Link to="/">首页</Link> },
    ];
    if (meta) {
      if (meta.group && GROUP_LABEL[meta.group]) {
        crumbs.push({ title: GROUP_LABEL[meta.group] });
      }
      if (location.pathname !== "/") {
        crumbs.push({ title: meta.label });
      }
    }
    return crumbs;
  }, [location.pathname]);

  const userMenuItems: MenuProps["items"] = [
    {
      key: "change-password",
      icon: <SafetyCertificateOutlined />,
      label: "修改密码",
      onClick: () => navigate("/me/change-password"),
    },
    { type: "divider" as const },
    {
      key: "logout",
      icon: <LogoutOutlined />,
      label: "退出登录",
      onClick: () => {
        clearSession();
        navigate("/login");
      },
    },
  ];

  const displayName = user?.displayName ?? user?.username ?? "未登录";

  return (
    <Layout style={{ minHeight: "100vh" }}>
      <Sider
        width={224}
        collapsedWidth={72}
        collapsed={collapsed}
        theme="dark"
        style={{
          background: token.colorBgContainer === "#FFFFFF" ? "#1F1A1B" : undefined,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* ── brand block ── */}
        <div
          style={{
            height: 56,
            padding: collapsed ? "0" : "0 18px",
            display: "flex",
            alignItems: "center",
            justifyContent: collapsed ? "center" : "flex-start",
            gap: 10,
            borderBottom: "1px solid rgba(255,255,255,0.08)",
          }}
        >
          <div
            style={{
              width: 30,
              height: 30,
              borderRadius: 8,
              background: token.colorPrimary,
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: 700,
              fontSize: 15,
              flexShrink: 0,
            }}
          >
            比
          </div>
          {collapsed ? null : (
            <div style={{ lineHeight: 1.1, minWidth: 0 }}>
              <div
                style={{
                  color: "#fff",
                  fontSize: 16,
                  fontWeight: 600,
                  letterSpacing: 0.5,
                }}
              >
                比个价
              </div>
              <div
                style={{
                  color: "rgba(245,245,245,0.5)",
                  fontSize: 11,
                  letterSpacing: 1,
                  marginTop: 2,
                }}
              >
                运营后台
              </div>
            </div>
          )}
        </div>

        {/* ── menu (scrolls) ── */}
        <div style={{ flex: 1, overflowY: "auto", paddingTop: 8 }}>
          <Menu
            theme="dark"
            mode="inline"
            items={items}
            selectedKeys={[location.pathname]}
            style={{ borderRight: 0, background: "transparent" }}
          />
        </div>

        {/* ── footer: collapse toggle + version ── */}
        <div
          style={{
            height: 44,
            borderTop: "1px solid rgba(255,255,255,0.08)",
            display: "flex",
            alignItems: "center",
            justifyContent: collapsed ? "center" : "space-between",
            padding: collapsed ? 0 : "0 16px",
            color: "rgba(245,245,245,0.45)",
            fontSize: 12,
          }}
        >
          <span
            role="button"
            aria-label="toggle-sider"
            onClick={() => setCollapsed((c) => !c)}
            style={{ cursor: "pointer", color: "rgba(245,245,245,0.7)", fontSize: 16 }}
          >
            {collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
          </span>
          {collapsed ? null : <span>Glow Price · {APP_VERSION}</span>}
        </div>
      </Sider>

      <Layout>
        <Header
          style={{
            background: token.colorBgContainer,
            borderBottom: `1px solid ${token.colorBorderSecondary}`,
            boxShadow: "0 1px 4px rgba(26,26,26,0.04)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "0 20px",
            position: "sticky",
            top: 0,
            zIndex: 20,
          }}
        >
          {/* left: collapse + breadcrumb */}
          <div style={{ display: "flex", alignItems: "center", gap: 14, minWidth: 0 }}>
            <span
              role="button"
              aria-label="toggle-sider-header"
              onClick={() => setCollapsed((c) => !c)}
              style={{ cursor: "pointer", fontSize: 18, color: token.colorTextSecondary }}
            >
              {collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            </span>
            <Breadcrumb items={breadcrumbItems} />
          </div>

          {/* right: search + bell + user */}
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <Input
              allowClear
              prefix={<SearchOutlined style={{ color: token.colorTextTertiary }} />}
              placeholder="搜索商品 / 渠道 / 品牌"
              style={{ width: 240 }}
            />
            <Tooltip title="通知">
              <Badge dot offset={[-2, 2]}>
                <BellOutlined
                  style={{ fontSize: 18, color: token.colorTextSecondary, cursor: "pointer" }}
                />
              </Badge>
            </Tooltip>
            <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
              <span
                style={{
                  cursor: "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "4px 6px",
                  borderRadius: 8,
                }}
              >
                <Avatar
                  size={30}
                  style={{ background: token.colorPrimary, color: "#fff" }}
                  icon={!user ? <UserOutlined /> : undefined}
                >
                  {user ? displayName.slice(0, 1) : null}
                </Avatar>
                <span style={{ fontSize: 14, color: token.colorText }}>{displayName}</span>
              </span>
            </Dropdown>
          </div>
        </Header>

        <Content style={{ padding: 20, background: token.colorBgLayout }}>
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
}
